import { REACT_APP_BASE_PATH } from "../config/runtimeConfig";
import { clearAuthSession } from "./clearAuthSession";

const STORAGE_KEY = "gar.saas.session";

/** Where the session JSON lives. The app uses localStorage; the embedded chat swaps in its own store. */
export type SessionStore = {
  load: () => string | null;
  save: (value: string) => void;
  /** Removes the session and anything else this store owns (the app store also drops OIDC leftovers). */
  clear: () => void;
};

const appSessionStore: SessionStore = {
  load: () => localStorage.getItem(STORAGE_KEY),
  save: (value) => localStorage.setItem(STORAGE_KEY, value),
  clear: () => {
    localStorage.removeItem(STORAGE_KEY);
    clearAuthSession();
  },
};

let sessionStore: SessionStore = appSessionStore;

export function configureSessionStore(store: SessionStore): void {
  sessionStore = store;
  refreshInFlight = null;
}

/** Store backed by one key of a Web Storage area; falls back to memory when storage is unavailable. */
export function webStorageSessionStore(storage: () => Storage, key: string): SessionStore {
  let memory: string | null = null;
  return {
    load: () => {
      try {
        return storage().getItem(key);
      } catch {
        return memory;
      }
    },
    save: (value) => {
      memory = value;
      try {
        storage().setItem(key, value);
      } catch {
        // partitioned / blocked storage: keep the in-memory copy
      }
    },
    clear: () => {
      memory = null;
      try {
        storage().removeItem(key);
      } catch {
        // ignore
      }
    },
  };
}

let refreshInFlight: Promise<ReturnType<typeof toUser> | null> | null = null;

/** Fired on window whenever the stored session is saved or cleared (login, refresh, logout). */
export const SESSION_CHANGED_EVENT = "gar:session-changed";

function notifySessionChanged() {
  window.dispatchEvent(new Event(SESSION_CHANGED_EVENT));
}

export type SaasSession = {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  token_type?: string;
  expires_at: number;
  profile?: Record<string, unknown>;
};

function baseUrl(): string {
  return (REACT_APP_BASE_PATH || "").replace(/\/+$/, "");
}

function decodeJwtPayload(jwt?: string | null): Record<string, any> | null {
  if (!jwt) return null;
  try {
    const parts = jwt.split(".");
    if (parts.length < 2) return null;
    let b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = (4 - (b64.length % 4)) % 4;
    if (pad) b64 += "=".repeat(pad);
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
}

function toUser(session: SaasSession) {
  const profile = session.profile || decodeJwtPayload(session.access_token) || {};
  return {
    access_token: session.access_token,
    refresh_token: session.refresh_token,
    id_token: session.id_token,
    token_type: session.token_type || "Bearer",
    expires_at: session.expires_at,
    expired: Date.now() / 1000 > session.expires_at - 30,
    profile,
  };
}

export function loadSession(): SaasSession | null {
  try {
    const raw = sessionStore.load();
    if (!raw) return null;
    return JSON.parse(raw) as SaasSession;
  } catch {
    return null;
  }
}

export function saveSessionFromTokens(payload: {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
  tokenType?: string;
  expiresIn?: number;
}): SaasSession {
  const expiresIn = payload.expiresIn && payload.expiresIn > 0 ? payload.expiresIn : 300;
  const session: SaasSession = {
    access_token: payload.accessToken,
    refresh_token: payload.refreshToken,
    id_token: payload.idToken,
    token_type: payload.tokenType || "Bearer",
    expires_at: Math.floor(Date.now() / 1000) + expiresIn,
    profile: decodeJwtPayload(payload.accessToken) || undefined,
  };
  sessionStore.save(JSON.stringify(session));
  notifySessionChanged();
  return session;
}

export function clearSaasSession() {
  sessionStore.clear();
  notifySessionChanged();
}

export class AuthApiError extends Error {
  /** HTTP status; 0 when the server could not be reached. */
  readonly status: number;
  /** Backend BusinessErrorCode (e.g. AUTH_EMAIL_TAKEN), or NETWORK_ERROR. */
  readonly code?: string;

  constructor(message: string, status: number, code?: string | { code?: string; name?: string }) {
    super(message);
    this.name = "AuthApiError";
    this.status = status;
    if (typeof code === "string") {
      this.code = code;
    } else if (code && typeof code === "object") {
      this.code = code.code || code.name;
    }
  }
}

async function postJson<T>(path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${baseUrl()}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new AuthApiError("Network error", 0, "NETWORK_ERROR");
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message =
      data?.response?.message || data?.message || data?.error || `Request failed (${res.status})`;
    throw new AuthApiError(message, res.status, data?.response?.businessErrorCode);
  }
  return data as T;
}

async function getJson<T>(path: string): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${baseUrl()}${path}`);
  } catch {
    throw new AuthApiError("Network error", 0, "NETWORK_ERROR");
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message =
      data?.response?.message || data?.message || data?.error || `Request failed (${res.status})`;
    throw new AuthApiError(message, res.status, data?.response?.businessErrorCode);
  }
  if (data && typeof data === "object" && "response" in data && (data as { response?: T }).response) {
    return (data as { response: T }).response;
  }
  return data as T;
}

export type InvitePreview = {
  workspaceName?: string;
  email?: string;
  role?: "TENANT_ADMIN" | "USER" | "REPORTER" | "VIEWER";
  status?: "PENDING" | "ACCEPTED" | "EXPIRED";
  accountExists?: boolean;
};

async function refreshOnce() {
  const current = loadSession();
  if (!current?.refresh_token) return null;
  try {
    const data = await postJson<TokenPayload>("/auth/refresh", { refreshToken: current.refresh_token });
    const session = saveSessionFromTokens({
      ...data,
      refreshToken: data.refreshToken || current.refresh_token,
    });
    return toUser(session);
  } catch {
    clearSaasSession();
    return null;
  }
}

export const sessionAuthApi = {
  register: (body: {
    email: string;
    username: string;
    password: string;
    workspaceName?: string;
    inviteToken?: string;
    firstName?: string;
    lastName?: string;
    captchaToken: string;
    captchaAnswer: string;
  }) =>
    postJson<{
      message: string;
      username: string;
      workspaceId: string;
      verificationRequired?: boolean;
      mailSent?: boolean;
      verificationToken?: string;
    }>("/auth/register", body),

  getCaptcha: () =>
    getJson<{ captchaToken: string; captchaImage?: string; prompt?: string; question?: string; expiresAt?: string }>(
      "/auth/captcha"
    ),

  previewInvite: (token: string) =>
    getJson<InvitePreview>(`/auth/invites/${encodeURIComponent(token)}`),

  verifyEmail: (token: string) => postJson<{ message: string }>("/auth/verify-email", { token }),

  login: async (username: string, password: string) => {
    const data = await postJson<{
      accessToken: string;
      refreshToken?: string;
      idToken?: string;
      tokenType?: string;
      expiresIn?: number;
    }>("/auth/login", { username, password });
    const session = saveSessionFromTokens(data);
    return toUser(session);
  },

  /** Single-flight: concurrent callers share one refresh (Keycloak rotates refresh tokens). */
  refresh: () => {
    if (!refreshInFlight) {
      refreshInFlight = refreshOnce().finally(() => {
        refreshInFlight = null;
      });
    }
    return refreshInFlight;
  },

  logout: async () => {
    const current = loadSession();
    try {
      if (current?.refresh_token) {
        await postJson("/auth/logout", { refreshToken: current.refresh_token });
      }
    } catch {
      // ignore
    }
    clearSaasSession();
  },

  forgotPassword: (email: string) => postJson<{ message: string }>("/auth/forgot-password", { email }),

  resetPassword: (token: string, newPassword: string) =>
    postJson<{ message: string }>("/auth/reset-password", { token, newPassword }),

  resendVerification: (email: string) =>
    postJson<{ message: string }>("/auth/resend-verification", { email }),

  getUser: async () => {
    const session = loadSession();
    if (!session) return null;
    if (Date.now() / 1000 > session.expires_at - 30) {
      return sessionAuthApi.refresh();
    }
    return toUser(session);
  },

  getAccessToken: async () => {
    const user = await sessionAuthApi.getUser();
    return user?.access_token ?? null;
  },

  /** Embedded chat sign-in: same credentials, plus the embed's workspace/role/origin rules. */
  embedLogin: async (body: { username: string; password: string; embedKey: string; parentOrigin: string }) => {
    const data = await postJson<TokenPayload>("/auth/embed/login", body);
    return toUser(saveSessionFromTokens(data));
  },

  /** Embedded chat sign-in with a one-time integration login ticket (no password). */
  embedTicketLogin: async (body: { ticket: string; embedKey: string; parentOrigin: string }) => {
    const data = await postJson<TokenPayload>("/auth/embed/ticket", body);
    return toUser(saveSessionFromTokens(data));
  },

  /** Embedded chat Google sign-in (PKCE); never creates a workspace. */
  embedOAuthExchange: async (body: {
    code: string;
    codeVerifier: string;
    redirectUri: string;
    embedKey: string;
    parentOrigin: string;
  }) => {
    const data = await postJson<TokenPayload>("/auth/embed/oauth/exchange", body);
    return toUser(saveSessionFromTokens(data));
  },

  /** Which sign-in providers the backend offers (Google is off for on-prem). */
  getAuthProviders: () => getJson<AuthProviders>("/auth/providers"),

  /** Exchanges the Google authorization code (PKCE) for a session. */
  oauthExchange: async (body: {
    code: string;
    codeVerifier: string;
    redirectUri: string;
    inviteToken?: string | null;
    workspaceName?: string | null;
  }) => {
    const data = await postJson<OAuthExchangeResult>("/auth/oauth/exchange", {
      ...body,
      workspaceName: body.workspaceName || undefined,
      inviteToken: body.inviteToken || undefined,
    });
    saveSessionFromTokens(data);
    return data;
  },

  /** Creates the first workspace for a Google user; needs the current bearer token. */
  completeWorkspaceOnboarding: async (workspaceName: string) => {
    const current = loadSession();
    if (!current?.refresh_token) {
      throw new AuthApiError("Your session has expired. Please sign in again.", 401, "SESSION_EXPIRED");
    }
    let res: Response;
    try {
      res = await fetch(`${baseUrl()}/account/onboarding/workspace`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `${current.token_type || "Bearer"} ${current.access_token}`,
        },
        body: JSON.stringify({ workspaceName, refreshToken: current.refresh_token }),
      });
    } catch {
      throw new AuthApiError("Network error", 0, "NETWORK_ERROR");
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const message =
        data?.response?.message || data?.message || data?.error || `Request failed (${res.status})`;
      throw new AuthApiError(message, res.status, data?.response?.businessErrorCode);
    }
    const session = saveSessionFromTokens({
      ...(data as TokenPayload),
      refreshToken: (data as TokenPayload).refreshToken || current.refresh_token,
    });
    return toUser(session);
  },
};

type TokenPayload = {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
  tokenType?: string;
  expiresIn?: number;
};

export type OAuthExchangeResult = TokenPayload & { onboardingRequired: boolean };

export type AuthProviders = {
  googleEnabled: boolean;
  keycloakAuthorizeUrl?: string | null;
  clientId?: string | null;
  googleIdpHint?: string | null;
};

export default sessionAuthApi;
